import type {PropertyDefinition, PropertyValue} from '@github-ui/custom-properties-types'
import {ownerPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ShieldLockIcon, UndoIcon} from '@primer/octicons-react'
import {Box, FormControl, IconButton, Link, Text} from '@primer/react'
import {type ReactNode, useMemo} from 'react'

import type {PropertyField} from '../hooks/use-edit-custom-properties'
import {CustomPropertyInput, DisplayPropertyValue, getControlLabel, ResetDropdown} from './CustomPropertyEditControl'

export function CustomPropertyValuesTable({
  definitions,
  editableDefinitions,
  propertyValuesMap,
  setPropertyValue,
  revertPropertyValue,
  showLockMessages = true,
  orgName,
  showUndo = true,
}: {
  definitions: PropertyDefinition[]
  editableDefinitions: PropertyDefinition[]
  propertyValuesMap: Record<string, PropertyField>
  setPropertyValue: (propertyName: string, value: PropertyValue) => void
  revertPropertyValue: (propertyName: string) => void
  showLockMessages?: boolean
  orgName: string
  showUndo?: boolean
}) {
  const editableDefinitionsSet = useMemo(
    () => new Set(editableDefinitions.map(definition => definition.propertyName)),
    [editableDefinitions],
  )

  return (
    <Box
      sx={{
        'div:last-child': {
          borderBottomWidth: 0,
        },
      }}
    >
      {definitions.map(definition => (
        <CustomPropertyValuesEditorRow
          key={definition.propertyName}
          definition={definition}
          field={propertyValuesMap[definition.propertyName]}
          setPropertyValue={setPropertyValue}
          revertPropertyValue={revertPropertyValue}
          viewOnly={!editableDefinitionsSet.has(definition.propertyName)}
          showLockMessages={showLockMessages}
          orgName={orgName}
          showUndo={showUndo}
        />
      ))}
    </Box>
  )
}

function CustomPropertyValuesEditorRow({
  definition,
  field,
  setPropertyValue,
  revertPropertyValue,
  viewOnly,
  showLockMessages,
  orgName,
  showUndo,
}: {
  definition: PropertyDefinition
  field?: PropertyField
  setPropertyValue: (propertyName: string, value: PropertyValue) => void
  revertPropertyValue: (propertyName: string) => void
  viewOnly: boolean
  showLockMessages: boolean
  orgName: string
  showUndo: boolean
}) {
  const {propertyName} = definition

  return (
    <Box
      sx={{
        px: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: 'border.default',
      }}
      data-testid="property-row"
    >
      {viewOnly ? (
        <CustomPropertyReadValueRow
          key={propertyName}
          propertyValue={field?.value || definition.defaultValue || ''}
          definition={definition}
          showLockMessages={showLockMessages}
          orgName={orgName}
        />
      ) : (
        <CustomPropertyEditValueRow
          key={propertyName}
          changed={!!field?.changed}
          mixed={!!field?.mixed}
          propertyValue={field?.value}
          definition={definition}
          error={field?.error}
          onChange={newValue => setPropertyValue(propertyName, newValue)}
          onReset={() => revertPropertyValue(propertyName)}
          showUndo={showUndo}
        />
      )}
    </Box>
  )
}

function CustomPropertyReadValueRow({
  propertyValue,
  definition,
  showLockMessages,
  orgName,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinition
  showLockMessages: boolean
  orgName: string
}) {
  const {description} = definition

  return (
    <DataTableRow
      label={getControlLabel(definition)}
      description={description}
      value={<DisplayPropertyValue propertyValue={propertyValue} definition={definition} />}
      bottomMessage={
        showLockMessages && (
          <Box sx={{display: 'inline'}}>
            <Text sx={{color: 'fg.subtle'}}>
              <ShieldLockIcon size={'small'} /> Managed by{' '}
            </Text>
            <Link inline href={ownerPath({owner: orgName})}>
              {orgName}
            </Link>
          </Box>
        )
      }
    />
  )
}

function CustomPropertyEditValueRow({
  propertyValue,
  definition,
  changed,
  mixed = false,
  error,
  onChange,
  onReset,
  showUndo,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinition
  changed: boolean
  mixed?: boolean
  error?: string
  onChange: (value: PropertyValue) => void
  onReset(): void
  showUndo: boolean
}) {
  const {propertyName, description} = definition
  const undoTitle = `Undo ${propertyName} changes`

  const editResetDropdownEnabled = useFeatureFlag('custom_properties_edit_reset_dropdown')

  return (
    <DataTableRow
      label={getControlLabel(definition)}
      description={description}
      value={
        <>
          <Box sx={{display: 'flex', gap: 2}}>
            <CustomPropertyInput
              propertyDefinition={definition}
              propertyValue={propertyValue}
              mixed={mixed}
              block={true}
              onChange={onChange}
            />
          </Box>
          {error && (
            <FormControl.Validation variant="error" sx={{pt: 2}}>
              {error}
            </FormControl.Validation>
          )}
        </>
      }
      trailing={
        <>
          {editResetDropdownEnabled ? (
            <ResetDropdown required={definition.required} {...{propertyName, changed, onReset, onChange, showUndo}} />
          ) : (
            <Box sx={{visibility: changed ? 'visible' : 'hidden'}}>
              <IconButton icon={() => <UndoIcon />} onClick={onReset} aria-label={undoTitle} variant="invisible" />
            </Box>
          )}
        </>
      }
    />
  )
}

const formControlStyle = {
  display: 'grid',
  flex: 1,
  py: 3,
  gridTemplateColumns: ['1fr auto', '1fr auto', '1fr 220px minmax(39px, auto)'],
  gridTemplateRows: ['auto auto auto auto', 'auto auto auto auto', 'auto auto auto'],
  gridTemplateAreas: [
    '"name trailing" "description trailing" "value trailing" "bottomMessage trailing"',
    '"name trailing" "description trailing" "value trailing" "bottomMessage trailing"',
    '"name value trailing" "description value trailing" "bottomMessage bottomMessage trailing"',
  ],
}

function DataTableRow({
  label,
  description,
  bottomMessage,
  value,
  trailing,
}: {
  label: string
  description: string | null
  bottomMessage?: ReactNode
  value: ReactNode
  trailing?: ReactNode
}) {
  return (
    <FormControl sx={formControlStyle}>
      <FormControl.Label
        sx={{gridArea: 'name', pr: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
        data-testid="property-name"
      >
        {label}
      </FormControl.Label>

      <Box
        sx={{
          gridArea: 'value',
          flexDirection: 'row',
          width: '220px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Box>

      {trailing && <Box sx={{gridArea: 'trailing', pl: 2}}>{trailing}</Box>}

      {description && (
        <FormControl.Caption sx={{gridArea: 'description', pr: 3, pb: [2, 2, 0]}}>{description}</FormControl.Caption>
      )}
      {bottomMessage && <Box sx={{gridArea: 'bottomMessage', mt: 1}}>{bottomMessage}</Box>}
    </FormControl>
  )
}

try{ CustomPropertyValuesTable.displayName ||= 'CustomPropertyValuesTable' } catch {}
try{ CustomPropertyValuesEditorRow.displayName ||= 'CustomPropertyValuesEditorRow' } catch {}
try{ CustomPropertyReadValueRow.displayName ||= 'CustomPropertyReadValueRow' } catch {}
try{ CustomPropertyEditValueRow.displayName ||= 'CustomPropertyEditValueRow' } catch {}
try{ DataTableRow.displayName ||= 'DataTableRow' } catch {}